// ============================================================================
// PollProtocolCards — ПРОТОКОЛЫ прохождения (результаты) карточками.
//
// Без таблиц: на телефоне и на десктопе одна и та же карточка — дата, объект
// тестирования, балл/порог, заключение, остальные поля (раскрываются).
// Значения форматируются единым lib/format (даты/числа/ссылки — серверные
// `_presentation`). Пустой список — явный текст, а не пустая рамка.
// ============================================================================

import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, ClipboardList } from 'lucide-react';
import { formatValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ProtocolColumn, ProtocolRow } from '@/lib/polls';

const PRIMARY_COLS = new Set([
  'created_at', 'entity_id', 'subject_id', 'score', 'passed', 'conclusion', 'updated_at',
]);

/** Единое форматирование значения протокола (даты, ссылки, boolean). */
function formatProtocolCell(col: ProtocolColumn, row: ProtocolRow): string {
  const presentation = row[col.name + '_presentation'];
  const value = presentation !== undefined && presentation !== null && presentation !== ''
    ? presentation
    : row[col.name];
  if (value === null || value === undefined || value === '') return '';
  if (col.name === 'passed') {
    const passed = value === true || value === 1 || String(value).toLowerCase() === 'true';
    return passed ? 'порог пройден' : '—';
  }
  const isDate = col.dataType === 'date' || col.dataType === 'timestamp' || col.dataType === 'datetime'
    || col.name === 'created_at' || col.name === 'updated_at';
  if (isDate) return formatValue(value, '', 'timestamp');
  return formatValue(value, '', col.dataType);
}

function passedOf(row: ProtocolRow): boolean {
  const v = row.passed;
  return v === true || v === 1 || String(v).toLowerCase() === 'true';
}

export interface PollProtocolCardsProps {
  columns: ProtocolColumn[];
  rows: ProtocolRow[];
  loading?: boolean;
  emptyText?: string;
  emptyAction?: ReactNode;
}

export function PollProtocolCards({ columns, rows, loading, emptyText, emptyAction }: PollProtocolCardsProps) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border bg-card px-3 py-3 text-xs">
        <div className="mb-1 flex items-center gap-1.5 text-sm font-medium">
          <ClipboardList className="h-3.5 w-3.5 text-primary" />
          Протоколы прохождения
        </div>
        <div className="text-muted-foreground">
          {loading
            ? 'Загрузка протоколов…'
            : emptyText || 'Протоколов пока нет — пройдите методику, и результат появится здесь.'}
        </div>
        {!loading && emptyAction && <div className="mt-2">{emptyAction}</div>}
      </div>
    );
  }

  const secondary = columns.filter((c) => !PRIMARY_COLS.has(c.name) || c.name === 'updated_at');

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        <ClipboardList className="h-3 w-3" />Протоколы прохождения
        <span className="normal-case tracking-normal">({rows.length})</span>
      </div>
      {rows.map((row, index) => {
        const key = String(row.id ?? index);
        const dateCol = columns.find((c) => c.name === 'created_at');
        const subjectCol = columns.find((c) => c.name === 'subject_id') || columns.find((c) => c.name === 'entity_id');
        const scoreCol = columns.find((c) => c.name === 'score');
        const conclusionCol = columns.find((c) => c.name === 'conclusion');
        const date = dateCol ? formatProtocolCell(dateCol, row) : '';
        const subject = subjectCol ? formatProtocolCell(subjectCol, row) : '';
        const score = scoreCol ? formatProtocolCell(scoreCol, row) : '';
        const conclusion = conclusionCol ? formatProtocolCell(conclusionCol, row) : '';
        const fields = secondary
          .map((c) => ({ label: c.label || c.name, value: formatProtocolCell(c, row) }))
          .filter((f) => f.value !== '');
        const expandedRow = expanded === key;

        return (
          <article key={key} className="rounded-lg border bg-card px-3 py-2">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-sm font-medium">{date || 'Дата не указана'}</span>
                  {subject && <span className="truncate text-[11px] text-muted-foreground">{subject}</span>}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap justify-end gap-1">
                {score && (
                  <span className="rounded-full border bg-muted/40 px-1.5 py-0.5 text-[10px]">балл: {score}</span>
                )}
                {row.passed !== undefined && row.passed !== null && row.passed !== '' && (
                  <span
                    className={cn(
                      'rounded-full border px-1.5 py-0.5 text-[10px]',
                      passedOf(row)
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                        : 'border-border bg-muted/50 text-muted-foreground',
                    )}
                  >
                    {passedOf(row) ? 'порог пройден' : 'порог не пройден'}
                  </span>
                )}
              </div>
            </div>

            {conclusion && (
              <div className="mt-1.5 whitespace-pre-wrap break-words text-xs text-foreground">{conclusion}</div>
            )}

            {fields.length > 0 && (
              <div className="mt-1.5">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                  onClick={() => setExpanded(expandedRow ? null : key)}
                  aria-expanded={expandedRow}
                >
                  {expandedRow ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  {expandedRow ? 'Скрыть поля' : 'Все поля'}
                </button>
                {expandedRow && (
                  <dl className="mt-1 grid gap-x-3 gap-y-1 text-[11px] sm:grid-cols-2">
                    {fields.map((f) => (
                      <div key={f.label} className="flex min-w-0 gap-1">
                        <dt className="shrink-0 text-muted-foreground">{f.label}:</dt>
                        <dd className="min-w-0 break-words">{f.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
